import type { Path } from '@shared/model/dataset';
import { pathElevationSeries, type ProfileStore } from '@shared/model/profile';
import { pluralize } from '@shared/format';
import { ElevationChart } from './ElevationChart';
import { Figures } from './Figures';
import { Steps } from './Steps';
import styles from './PathCard.module.css';

interface Props {
    path: Path;
    active: boolean;
    onToggle: (path: Path) => void;
    profiles: ProfileStore;
    profilesVersion: number;
    maxDistance: number;
    minAlt: number;
    maxAlt: number;
}

export function PathCard({ path, active, onToggle, profiles, profilesVersion, maxDistance, minAlt, maxAlt }: Props) {
    const loaded = profiles.hasPath(path);
    // Recomputed on every render: profilesVersion (a prop) changes when a profile arrives.
    void profilesVersion;
    const series = loaded ? pathElevationSeries(path, profiles) : [];
    const closedLegs = path.closedLegs;

    return (
        <article
            id={`path-${path.id}`}
            className={`${styles.card} ${active ? styles.active : ''} ${path.hasClosed ? styles.closed : ''}`}
            tabIndex={0}
            onClick={() => onToggle(path)}
            onKeyDown={(e) => {
                if (e.key === 'Enter') onToggle(path);
            }}
            aria-expanded={active}
            data-testid="path"
        >
            <h4 className={`title-bar ${styles.title}`}>{path.title}</h4>
            <Figures distance={path.distance} duration={path.duration} up={path.up} down={path.down} />
            <ElevationChart
                series={series}
                maxDistance={maxDistance}
                minAlt={minAlt}
                maxAlt={maxAlt}
                loading={!loaded}
            />
            {!active && (closedLegs.length > 0 || path.infos.length > 0) && (
                <div className={styles.badges}>
                    {closedLegs.length > 0 && (
                        <span className="badge badge--closed">
                            ⛔️{' '}
                            {closedLegs.length > 1
                                ? pluralize(closedLegs.length, 'sentier fermé', 'sentiers fermés')
                                : 'Un sentier fermé'}
                        </span>
                    )}
                    {closedLegs.map((leg) => {
                        const cause = leg.route.cause;
                        const reopen = leg.route.reopenDate;
                        if (!cause && !reopen) return null;
                        return (
                            <span key={leg.code} className="badge badge--closed-detail">
                                {cause}
                                {cause && reopen ? ' — ' : ''}
                                {reopen ? `Réouverture : ${reopen}` : ''}
                            </span>
                        );
                    })}
                    {path.infos.map((info) => (
                        <span key={info} className="badge badge--info">
                            ℹ️ {info}
                        </span>
                    ))}
                </div>
            )}
            {active && <Steps path={path} />}
            <div className={styles.toggleRow}>
                <span className={styles.toggle}>{active ? '▴ Masquer les étapes ▴' : '▾ Afficher les étapes ▾'}</span>
            </div>
        </article>
    );
}
